import React from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  IconButton,
  useTheme,
  alpha,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import dayjs from "dayjs";

// `onMenuClick` is optional — when provided together with `isMobile`, a small
// ⋮ button is rendered on the card (touch-friendly path to the context menu).
// All other consumers of TaskItem (profile screens) pass neither prop and keep
// rendering exactly as before.
const TaskItem = ({
  task,
  onClick,
  onContextMenu,
  onMenuClick,
  isMobile = false,
  provided,
  snapshot,
}) => {
  const theme = useTheme();
  const showMenuButton = Boolean(onMenuClick) && isMobile;

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case "Urgent":
        return theme.palette.error[400];
      case "High":
        return theme.palette.warning[400];
      case "Normal":
        return theme.palette.primary[400];
      case "Low":
        return theme.palette.grey[400];
      default:
        return theme.palette.grey[400];
    }
  };

  const getShadowPriorityColor = (priority) => {
    switch (priority) {
      case "Urgent":
        return theme.palette.error[600];
      case "High":
        return theme.palette.warning[600];
      case "Normal":
        return theme.palette.primary[600];
      case "Low":
        return theme.palette.grey[600];
      default:
        return theme.palette.grey[600];
    }
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return "No due date";
    return dayjs(date).format("MMM DD, YYYY");
  };

  // Get due date color based on status
  const getDueDateColor = (date) => {
    if (!date) return theme.palette.primary.text2;
    const today = dayjs().startOf("day");
    const dueDate = dayjs(date).startOf("day");

    if (dueDate.isSame(today)) {
      return theme.palette.error[400]; // Due today
    } else if (dueDate.isBefore(today)) {
      return theme.palette.warning[400]; // Overdue
    }
    return theme.palette.primary.text2; // Future date
  };

  return (
    <Box
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      sx={{
        marginBottom: "12px",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        ...provided.draggableProps.style,
      }}
    >
      <Card
        onClick={(e) => {
          // Only trigger onClick if not dragging
          if (!snapshot.isDragging) {
            onClick(e);
          }
        }}
        onContextMenu={(e) => {
          // Only trigger context menu if not dragging
          if (!snapshot.isDragging && onContextMenu) {
            onContextMenu(e);
          }
        }}
        sx={{
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          bgcolor: snapshot.isDragging
            ? theme.palette.background.alt
            : theme.palette.background.default,
          borderRadius: "12px",
          //border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.shadows[3],
          cursor: isMobile
            ? "pointer"
            : snapshot.isDragging
              ? "grabbing"
              : "grab",
          position: "relative",
          overflow: "hidden",
          "&:hover": {
            backgroundColor: theme.palette.primary.hover,
            //boxShadow: theme.shadows[5],
            boxShadow: `
              0px 3px 5px -1px ${alpha(getShadowPriorityColor(task.Priority), 0.2)},
              0px 6px 10px 0px ${alpha(getShadowPriorityColor(task.Priority), 0.14)},
              0px 1px 18px 0px ${alpha(getShadowPriorityColor(task.Priority), 0.12)}
            `,
          },
        }}
      >
        {/* Priority color indicator on right edge */}
        <Box
          sx={{
            position: "absolute",
            height: "5%",
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: getPriorityColor(task.Priority),
            borderTopRightRadius: "12px",
            borderBottomRightRadius: "12px",
            borderTopLeftRadius: "0px",
            borderBottomLeftRadius: "0px",
          }}
        />
        {/* Touch-friendly ⋮ actions button (mobile only, opt-in via prop) */}
        {showMenuButton && (
          <IconButton
            size="small"
            aria-label="Task actions"
            onClick={(e) => {
              // Never let the tap bubble up to the card (open task) handler
              e.stopPropagation();
              e.preventDefault();
              onMenuClick(e, task);
            }}
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              zIndex: 1,
              width: 28,
              height: 28,
              p: 0.25,
              color: theme.palette.primary.text2,
              "&:hover": {
                color: theme.palette.primary.main,
                bgcolor: theme.palette.action.hover,
              },
            }}
          >
            <MoreVertRoundedIcon fontSize="small" />
          </IconButton>
        )}
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 1,
              // Reserve the top-right corner for the ⋮ button so it never
              // overlaps the service/entity chip
              pr: showMenuButton ? "24px" : 0,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: "bold",
                color: theme.palette.primary.main,
                flex: 1,
              }}
            >
              {task.Title}
            </Typography>
            {(task.ServiceType || task.EntityType) && (
              <Chip
                label={task.ServiceType || task.EntityType}
                size="small"
                variant="outlined"
                sx={{
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  fontSize: "0.7rem",
                  height: "20px",
                  ml: 1,
                  borderRadius: "6px",
                }}
              />
            )}
          </Box>

          {task.Description && (
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.primary.text2,
                mb: 1.5,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {task.Description}
            </Typography>
          )}

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
            {task.IsCancelled && (
              <Chip
                label="Cancelled"
                size="small"
                variant="outlined"
                sx={{
                  color: theme.palette.error.main,
                  borderColor: theme.palette.error.main,
                  fontSize: "0.65rem",
                  height: "18px",
                  borderRadius: "6px",
                }}
              />
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: getDueDateColor(task.DueDate),
                fontWeight:
                  task.DueDate &&
                  (dayjs(task.DueDate).isSame(dayjs(), "day") ||
                    dayjs(task.DueDate).isBefore(dayjs(), "day"))
                    ? 600
                    : 400,
              }}
            >
              {formatDate(task.DueDate)}
            </Typography>

            {task.IsAutoGenerated && (
              <AutoAwesomeRoundedIcon
                sx={{
                  fontSize: 16,
                  color: theme.palette.primary.main,
                }}
              />
            )}

            {task.assignedTo && (
              <Typography
                variant="caption"
                sx={{ color: theme.palette.primary.text2 }}
              >
                {task.assignedTo.Name}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TaskItem;
